/** Save the seven-level draft; ship the compressed four. */
import {
  RouteGetMerchflows_merchflowId_CdtDraft,
  RoutePutMerchflows_merchflowId_CdtDraft,
  type PutMerchflowCdtDraft,
  type ResponseGetMerchflowCdtDraft,
} from "@CommonApi/merchflows/_merchflowId_/cdt/draft";
import {
  CdtCompressionRejectionSchema,
  RoutePostMerchflows_merchflowId_CdtCompression,
  type PostMerchflowCdtCompression,
  type ResponsePostMerchflowCdtCompression,
  type UnplacedCdtCombo,
} from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { API, getRequest, postRequest, putRequest } from "src/utils/axios";
import { queryClient } from "src/utils/queryClient";
import { STUDIO_ATTRIBUTES_QUERY_ROOT } from "./useStudioAttributesQuery";

/** Bump when the draft's SHAPE changes, and add a matching adapter branch. */
export const CDT_DRAFT_SCHEMA_VERSION = "1.0";

const url = (route: string, merchflowId: number) =>
  `${API}${route.replace(":merchflowId", String(merchflowId))}`;

export const fetchCdtDraft = async (
  merchflowId: number,
): Promise<ResponseGetMerchflowCdtDraft | null> => {
  try {
    const { data } = await getRequest<ResponseGetMerchflowCdtDraft>(
      url(RouteGetMerchflows_merchflowId_CdtDraft, merchflowId),
    );
    return data;
  } catch (error) {
    // 404 is the normal "never saved" case; anything else is a real error.
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
};

export const saveCdtDraft = async (
  merchflowId: number,
  payload: PutMerchflowCdtDraft,
): Promise<ResponseGetMerchflowCdtDraft> => {
  const { data } = await putRequest<ResponseGetMerchflowCdtDraft>(
    url(RoutePutMerchflows_merchflowId_CdtDraft, merchflowId),
    { payload },
  );
  return data;
};

/** Throws on 422. `acknowledgeUnplacedCombos` overrides the unplaced-combo refusal only. */
export const shipCdtCompression = async (
  merchflowId: number,
  payload: PostMerchflowCdtCompression,
  acknowledgeUnplacedCombos = false,
): Promise<ResponsePostMerchflowCdtCompression> => {
  const { data } = await postRequest<ResponsePostMerchflowCdtCompression>(
    url(RoutePostMerchflows_merchflowId_CdtCompression, merchflowId),
    { payload: { ...payload, acknowledgeUnplacedCombos } },
  );
  // A ship makes the item master this studio inferred from stale.
  void queryClient.invalidateQueries({ queryKey: [STUDIO_ATTRIBUTES_QUERY_ROOT] });
  return data;
};

const responseDataOf = (error: unknown): Record<string, unknown> | undefined => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : undefined;
};

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const firstString = (...candidates: unknown[]): string | undefined =>
  candidates.find((candidate): candidate is string => typeof candidate === "string" && !!candidate);

/** Combos carried on a 422; `[]` for any other refusal, so no override is offered. */
export const unplacedCombosOf = (error: unknown): UnplacedCdtCombo[] => {
  const body = responseDataOf(error);
  if (!Array.isArray(body?.unresolvedCombos)) return [];
  return body.unresolvedCombos.filter(
    (combo): combo is UnplacedCdtCombo =>
      typeof combo === "object" &&
      combo !== null &&
      Array.isArray((combo as UnplacedCdtCombo).productCodes),
  );
};

export const unplacedProductCodesOf = (combos: readonly UnplacedCdtCombo[]): string[] => [
  ...new Set(combos.flatMap((combo) => combo.productCodes)),
];

/** 422 spells its message `errorMessage`; 404/409 spell it `{ error }`. */
export const describeShipFailure = (error: unknown): string => {
  const body = responseDataOf(error);
  const rejection = CdtCompressionRejectionSchema.safeParse(body);

  const combos = rejection.success ? rejection.data.unresolvedCombos : unplacedCombosOf(error);
  const unmatched = rejection.success
    ? rejection.data.unmatchedProductCodes
    : stringsOf(body?.unmatchedProductCodes);

  const detail = combos.length
    ? `${unplacedProductCodesOf(combos).length} product(s) in ${combos
        .map((combo) => `${combo.cdt0} / ${combo.cdt1}`)
        .join(", ")}`
    : unmatched.slice(0, 5).join(", ");

  const message = rejection.success
    ? rejection.data.errorMessage
    : (firstString(body?.errorMessage, body?.error) ??
      (error instanceof Error ? error.message : String(error)));

  return detail ? `${message}: ${detail}` : message;
};
